"use client";

import clsx from "clsx";

/**
 * A6 — Brand micro-illustration: a stylised bank vault for T2 4.13 +
 * T3 5.15 (LC issued). Pairs with the bank-badge corner stamp so the
 * "Letter of Credit · issued" headline reads visually as "the bank
 * has locked the payment in" instead of just text. Mirrors the
 * LogisticsInsurancePicker's Maersk container-ship visual treatment
 * — same height band, same brand-color hull / accent split.
 *
 * Bank-aware: FAB renders with FAB-blue accents, Itaú with Itaú-orange.
 * Falls back to state-financed (gold) when the bank isn't known.
 */
export function VaultIllustration({
  bankBadge,
  className,
}: {
  bankBadge?: "FAB" | "Itau" | null;
  className?: string;
}) {
  const isFab = bankBadge === "FAB";
  const isItau = bankBadge === "Itau";
  const accent     = isFab  ? "#0F4F8C" : isItau ? "#EC7000" : "var(--state-financed)";
  const accentDeep = isFab  ? "#0A3563" : isItau ? "#B85800" : "var(--orbitra-gold)";
  const wordmark   = isFab  ? "FAB"     : isItau ? "ITAÚ"    : "BANK";

  return (
    <div className={clsx("rounded-md border border-state-financed/30 bg-orbitra-ink/40 p-3", className)}>
      <div className="flex items-center justify-between">
        <p className="font-mono text-[9px] uppercase tracking-[0.18em] text-state-financed">
          LC issued · payment locked
        </p>
        <p className="font-mono text-[9px] text-orbitra-muted">
          {wordmark} · vault
        </p>
      </div>
      <div className="relative mt-2 h-20 overflow-hidden rounded-md">
        {/* Marble floor band */}
        <div className="absolute inset-x-0 bottom-0 h-6 bg-gradient-to-b from-orbitra-ink/60 to-orbitra-ink/90" />
        <svg viewBox="0 0 320 80" className="absolute inset-0 h-full w-full">
          {/* Vault body — rounded square with riveted edges */}
          <rect x="100" y="14" width="120" height="50" rx="6" fill={accent} />
          <rect x="100" y="14" width="120" height="6" rx="3" fill={accentDeep} opacity="0.8" />
          <rect x="100" y="58" width="120" height="6" rx="3" fill={accentDeep} opacity="0.8" />

          {/* Vault rivets — 6 around the edge */}
          {[110, 130, 150, 170, 190, 210].map((x) => (
            <circle key={`top-${x}`} cx={x} cy="18" r="1.5" fill="rgba(255,255,255,0.6)" />
          ))}
          {[110, 130, 150, 170, 190, 210].map((x) => (
            <circle key={`bot-${x}`} cx={x} cy="60" r="1.5" fill="rgba(255,255,255,0.6)" />
          ))}

          {/* Central dial — circle with cardinal-point tick marks */}
          <circle cx="160" cy="39" r="14" fill={accentDeep} opacity="0.9" />
          <circle cx="160" cy="39" r="10" fill="none" stroke="rgba(255,255,255,0.8)" strokeWidth="0.8" />
          {/* Tick marks at N/E/S/W */}
          <line x1="160" y1="27" x2="160" y2="30" stroke="#FFFFFF" strokeWidth="1" />
          <line x1="160" y1="48" x2="160" y2="51" stroke="#FFFFFF" strokeWidth="1" />
          <line x1="148" y1="39" x2="151" y2="39" stroke="#FFFFFF" strokeWidth="1" />
          <line x1="169" y1="39" x2="172" y2="39" stroke="#FFFFFF" strokeWidth="1" />
          {/* Dial hand pointing NE — "locked" position */}
          <line x1="160" y1="39" x2="168" y2="33" stroke="#FFFFFF" strokeWidth="1.5" strokeLinecap="round" />
          {/* Center dot */}
          <circle cx="160" cy="39" r="1.5" fill="#FFFFFF" />

          {/* Handle bar — five-pronged on the side */}
          <g stroke="rgba(255,255,255,0.7)" strokeWidth="1.2" strokeLinecap="round">
            <line x1="195" y1="39" x2="208" y2="32" />
            <line x1="195" y1="39" x2="212" y2="35" />
            <line x1="195" y1="39" x2="215" y2="39" />
            <line x1="195" y1="39" x2="212" y2="43" />
            <line x1="195" y1="39" x2="208" y2="46" />
            <circle cx="195" cy="39" r="2" fill="rgba(255,255,255,0.85)" />
          </g>

          {/* Wordmark on the door, below the dial */}
          <text
            x="160"
            y="58"
            textAnchor="middle"
            fontFamily="ui-monospace, SFMono-Regular, monospace"
            fontSize="7"
            fontWeight="700"
            letterSpacing="2"
            fill="rgba(255,255,255,0.92)"
          >
            {wordmark}
          </text>

          {/* Floor shadow */}
          <ellipse cx="160" cy="68" rx="64" ry="3" fill="rgba(0,0,0,0.3)" />
        </svg>
      </div>
    </div>
  );
}
